import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { type StripeEnv, createStripeClient, getStripeErrorMessage } from "@/lib/stripe.server";

type CheckoutSessionResult =
  | { clientSecret: string }
  | { error: string; code?: "already_subscribed"; portalUrl?: string };
type PortalSessionResult = { url: string } | { error: string };
type EnsureTaxCodesResult = { updated: string[] } | { error: string };

async function tryCreatePortalUrl(
  stripe: ReturnType<typeof createStripeClient>,
  customerId: string,
  returnUrl: string,
): Promise<string | undefined> {
  try {
    const portal = await stripe.billingPortal.sessions.create({
      customer: customerId,
      return_url: returnUrl,
    });
    return portal.url;
  } catch (e) {
    console.error("portal session create failed in checkout guard", e);
    return undefined;
  }
}

const SUPPORT_ALERT_EMAIL = "support@tilt.university";

async function logCheckoutBlock(params: {
  userId: string;
  userEmail: string | undefined;
  environment: StripeEnv;
  priceId: string;
  reason: string;
  existingStatus?: string | null;
  existingCustomerId?: string | null;
  existingSubscriptionId?: string | null;
}): Promise<void> {
  // Best-effort: never let logging or alerting break checkout responses.
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error: insertError } = await supabaseAdmin
      .from("checkout_block_log")
      .insert({
        user_id: params.userId,
        user_email: params.userEmail ?? null,
        environment: params.environment,
        attempted_price_id: params.priceId,
        block_reason: params.reason,
        existing_status: params.existingStatus ?? null,
        existing_stripe_customer_id: params.existingCustomerId ?? null,
        existing_stripe_subscription_id: params.existingSubscriptionId ?? null,
      })
      .select("id")
      .single();

    if (insertError) {
      console.error("checkout_block_log insert failed", insertError);
    }

    // Fire support alert (queued via existing email infra) so the block is
    // visible outside the DB. If the queue isn't configured, this quietly
    // no-ops — the DB row is still the source of truth.
    try {
      const subject = `[SmartEssay] Checkout blocked — ${params.userEmail ?? params.userId}`;
      const bodyLines = [
        `A user was blocked from starting checkout.`,
        ``,
        `User: ${params.userEmail ?? "(no email)"} (${params.userId})`,
        `Environment: ${params.environment}`,
        `Attempted price: ${params.priceId}`,
        `Reason: ${params.reason}`,
        `Existing status: ${params.existingStatus ?? "n/a"}`,
        `Stripe customer: ${params.existingCustomerId ?? "n/a"}`,
        `Stripe subscription: ${params.existingSubscriptionId ?? "n/a"}`,
        `Time: ${new Date().toISOString()}`,
      ];
      const text = bodyLines.join("\n");
      const html = `<pre style="font:14px/1.5 ui-monospace,Menlo,monospace;white-space:pre-wrap">${text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")}</pre>`;

      await supabaseAdmin.rpc("enqueue_email", {
        queue_name: "transactional_emails",
        payload: {
          to: SUPPORT_ALERT_EMAIL,
          from: `alerts@smartessay.ai`,
          sender_domain: "smartessay.ai",
          subject,
          html,
          text,
          purpose: "transactional",
          label: "checkout_block_alert",
          idempotency_key: row?.id ?? `${params.userId}:${Date.now()}`,
          message_id: row?.id ?? `${params.userId}:${Date.now()}`,
        },
      });

      if (row?.id) {
        await supabaseAdmin
          .from("checkout_block_log")
          .update({ notified: true })
          .eq("id", row.id);
      }
    } catch (alertErr) {
      console.error("checkout_block_log alert enqueue failed", alertErr);
    }
  } catch (err) {
    console.error("logCheckoutBlock failed", err);
  }
}



const PRODUCT_TAX_CODES: Record<string, string> = {
  // SaaS — Electronically supplied services
  smartessay_pro: "txcd_10103001",
};

async function resolveOrCreateCustomer(
  stripe: ReturnType<typeof createStripeClient>,
  options: { email?: string; userId?: string },
): Promise<string> {
  if (options.userId && !/^[a-zA-Z0-9_-]+$/.test(options.userId)) {
    throw new Error("Invalid userId");
  }
  if (options.userId) {
    const found = await stripe.customers.search({
      query: `metadata['userId']:'${options.userId}'`,
      limit: 1,
    });
    if (found.data.length) return found.data[0].id;
  }
  if (options.email) {
    const existing = await stripe.customers.list({ email: options.email, limit: 1 });
    if (existing.data.length) {
      const customer = existing.data[0];
      if (options.userId && customer.metadata?.userId !== options.userId) {
        await stripe.customers.update(customer.id, {
          metadata: { ...customer.metadata, userId: options.userId },
        });
      }
      return customer.id;
    }
  }
  const created = await stripe.customers.create({
    ...(options.email && { email: options.email }),
    ...(options.userId && { metadata: { userId: options.userId } }),
  });
  return created.id;
}

export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: {
    priceId: string;
    returnUrl: string;
    environment: StripeEnv;
  }) => {
    if (!/^[a-zA-Z0-9_-]+$/.test(data.priceId)) throw new Error("Invalid priceId");
    return data;
  })
  .handler(async ({ data, context }): Promise<CheckoutSessionResult> => {
    try {
      const { supabase, userId } = context;
      const { data: { user } } = await supabase.auth.getUser();
      const email = user?.email ?? undefined;

      // Guard: block if the user already has an active/trialing/past_due sub
      // in this env. Plan switches must go through the billing portal.
      const { data: existing } = await supabase
        .from("subscriptions")
        .select("status,current_period_end,cancel_at_period_end,stripe_customer_id,stripe_subscription_id")
        .eq("user_id", userId)
        .eq("environment", data.environment)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (existing) {
        const periodEnd = existing.current_period_end
          ? new Date(existing.current_period_end as string).getTime()
          : null;
        const future = periodEnd === null || periodEnd > Date.now();
        const isActive =
          ["active", "trialing", "past_due"].includes(existing.status as string) && future;
        if (isActive) {
          await logCheckoutBlock({
            userId,
            userEmail: email,
            environment: data.environment,
            priceId: data.priceId,
            reason: "db_active_subscription",
            existingStatus: existing.status as string,
            existingCustomerId: existing.stripe_customer_id as string | null,
            existingSubscriptionId: existing.stripe_subscription_id as string | null,
          });
          let portalUrl: string | undefined;
          const customerId = existing.stripe_customer_id as string | null;
          if (customerId) {
            const stripe = createStripeClient(data.environment);
            let origin = "";
            try { origin = new URL(data.returnUrl).origin; } catch { /* ignore */ }
            portalUrl = await tryCreatePortalUrl(
              stripe,
              customerId,
              origin ? `${origin}/pricing` : data.returnUrl,
            );
          }
          return {
            error: "You already have an active subscription. Open the billing portal to switch plans, update your card, or cancel.",
            code: "already_subscribed",
            portalUrl,
          };
        }
      }


      const stripe = createStripeClient(data.environment);

      const prices = await stripe.prices.list({ lookup_keys: [data.priceId] });
      if (!prices.data.length) throw new Error("Price not found");
      const stripePrice = prices.data[0];
      const isRecurring = stripePrice.type === "recurring";

      const customerId = await resolveOrCreateCustomer(stripe, { email, userId });

      // Stripe-side guard: prevent double-billing when the same person
      // signs up with a second account (different user_id, same email →
      // same Stripe customer). The DB guard above only catches the same
      // user_id. Check the customer's live Stripe subscriptions and
      // refuse if any recurring one is still billing.
      if (isRecurring) {
        const targetProductId = typeof stripePrice.product === "string"
          ? stripePrice.product
          : stripePrice.product.id;
        const activeStatuses = new Set(["active", "trialing", "past_due", "unpaid"]);
        for (const status of ["active", "trialing", "past_due", "unpaid"] as const) {
          const subs = await stripe.subscriptions.list({
            customer: customerId,
            status,
            limit: 100,
          });
          // Only block on subs for THIS product — the same Stripe customer
          // may hold subs for sibling products (e.g. Smart-Reader) that
          // must not gate SmartEssay checkout.
          const conflict = subs.data.find((s) =>
            activeStatuses.has(s.status)
            && !s.cancel_at_period_end
            && s.items.data.some((it) => {
              const pid = typeof it.price.product === "string" ? it.price.product : it.price.product.id;
              return pid === targetProductId;
            }),
          );
          if (conflict) {
            await logCheckoutBlock({
              userId,
              userEmail: email,
              environment: data.environment,
              priceId: data.priceId,
              reason: "stripe_active_subscription",
              existingStatus: conflict.status,
              existingCustomerId: customerId,
              existingSubscriptionId: conflict.id,
            });
            let origin = "";
            try { origin = new URL(data.returnUrl).origin; } catch { /* ignore */ }
            const portalUrl = await tryCreatePortalUrl(
              stripe,
              customerId,
              origin ? `${origin}/pricing` : data.returnUrl,
            );
            return {
              error:
                "You already have an active subscription for this product. Open the billing portal to switch plans, update your card, or cancel.",
              code: "already_subscribed",
              portalUrl,
            };
          }

        }
      }


      let productDescription: string | undefined;
      if (!isRecurring) {
        const productId = typeof stripePrice.product === "string"
          ? stripePrice.product
          : stripePrice.product.id;
        const product = await stripe.products.retrieve(productId);
        productDescription = product.name;
      }

      const session = await stripe.checkout.sessions.create({
        line_items: [{ price: stripePrice.id, quantity: 1 }],
        mode: isRecurring ? "subscription" : "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer: customerId,
        // End-to-end tax + fraud + dispute handling by Stripe (+3.5%).
        managed_payments: { enabled: true },
        ...(!isRecurring && { payment_intent_data: { description: productDescription } }),
        metadata: { userId },
        ...(isRecurring && { subscription_data: { metadata: { userId } } }),
      } as any);

      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

export const createPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { returnUrl?: string; environment: StripeEnv }) => data)
  .handler(async ({ data, context }): Promise<PortalSessionResult> => {
    const { supabase, userId } = context;

    const { data: sub, error: subError } = await supabase
      .from("subscriptions")
      .select("stripe_customer_id")
      .eq("user_id", userId)
      .eq("environment", data.environment)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (subError || !sub?.stripe_customer_id) {
      return { error: "No subscription found" };
    }

    try {
      const stripe = createStripeClient(data.environment);
      const portal = await stripe.billingPortal.sessions.create({
        customer: sub.stripe_customer_id as string,
        ...(data.returnUrl && { return_url: data.returnUrl }),
      });
      return { url: portal.url };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

// One-shot: set the correct tax_code on each known product so managed
// payments can classify tax correctly. Safe to call repeatedly — Stripe
// just overwrites the same value. Idempotent and only touches a fixed
// allow-list of product IDs, so it's safe to leave un-gated.
export const ensureProductTaxCodes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { environment: StripeEnv }) => data)
  .handler(async ({ data }): Promise<EnsureTaxCodesResult> => {
    try {
      const stripe = createStripeClient(data.environment);
      const updated: string[] = [];
      for (const [productId, taxCode] of Object.entries(PRODUCT_TAX_CODES)) {
        try {
          await stripe.products.update(productId, { tax_code: taxCode });
          updated.push(productId);
        } catch (e) {
          console.error(`Failed to set tax_code on ${productId}:`, e);
        }
      }
      return { updated };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });
